import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, ErrorState, cn } from '@mustauto/ui/web';
import { PACKAGE_MODULE, VEHICLE_SIZES, VEHICLE_SIZE_LABELS, type VehicleSize } from '@mustauto/types';
import { formatPackageHours, formatWarrantyMonths, packagePriceForSize } from '@mustauto/utils';
import { api } from '@/lib/api';
import { formatReais } from '@/lib/utils';
import type { LaborPrice } from '@/hooks/useSettings';

/** A lista de pacotes (a mesma chave da aba Mão de Obra: um cache só). */
export function useEstheticPackages() {
  return useQuery<{ prices: LaborPrice[] }, unknown, LaborPrice[]>({
    queryKey: ['labor-prices'],
    // Mesma URL do `useLaborPrices` (mesma chave de cache = mesma resposta).
    queryFn: async () => (await api.get('/settings/labor-prices?withPackageCatalog=true')).data,
    select: (data) => data.prices.filter((p) => p.module === PACKAGE_MODULE),
  });
}

/**
 * Escolha do pacote na abertura da OS de estética pelo painel (F2.2): porte +
 * pacotes. Quem calcula o preço de verdade é a API (`packageLaborItem`), com
 * o mesmo porte — o que aparece aqui é a prévia.
 */
export function OSPackagePicker({
  packages,
  selected,
  size,
  suggestedSize,
  onToggle,
  onSizeChange,
  isLoading,
  isError,
  error,
  onRetry,
}: {
  packages: LaborPrice[];
  selected: string[];
  size: VehicleSize | null;
  suggestedSize: VehicleSize | null;
  onToggle: (id: string) => void;
  onSizeChange: (size: VehicleSize | null) => void;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  // Preço sugerido que o gestor ainda não confirmou não abre OS (a API recusa).
  const pending = packages.filter((p) => p.pendingConfirmation).length;
  const available = packages.filter((p) => !p.pendingConfirmation);
  const total = available
    .filter((p) => selected.includes(p.id))
    .reduce((sum, p) => sum + packagePriceForSize(p.basePrice, p.sizePrices, size), 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Pacote</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="mb-2 text-xs text-muted-foreground">Porte do veículo</p>
          <div role="radiogroup" aria-label="Porte do veículo" className="flex flex-wrap gap-2">
            {VEHICLE_SIZES.map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={size === s}
                onClick={() => onSizeChange(size === s ? null : s)}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-sm font-medium',
                  size === s ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted',
                )}
              >
                {VEHICLE_SIZE_LABELS[s]}
              </button>
            ))}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {size
              ? size === suggestedSize
                ? 'Sugerido pelo modelo do carro.'
                : 'Os preços abaixo são deste porte.'
              : 'Sem porte, vale o preço base de cada pacote.'}
          </p>
        </div>

        {isLoading ? (
          <p className="text-sm text-muted-foreground">Carregando pacotes…</p>
        ) : isError && available.length === 0 ? (
          <ErrorState title="Não foi possível carregar os pacotes" error={error} onRetry={onRetry} />
        ) : available.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {pending > 0
              ? `${pending === 1 ? "O pacote cadastrado ainda espera" : `Os ${pending} pacotes cadastrados ainda esperam`} a confirmação do preço em Configurações → Mão de Obra. Até lá, descreva o serviço abaixo.`
              : 'Nenhum pacote cadastrado. Cadastre em Configurações → Mão de Obra, ou descreva o serviço abaixo.'}
          </p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {available.map((p) => {
              const on = selected.includes(p.id);
              const meta = [
                p.durationHours ? formatPackageHours(p.durationHours) : null,
                p.warrantyMonths ? `garantia de ${formatWarrantyMonths(p.warrantyMonths)}` : null,
              ]
                .filter(Boolean)
                .join(' · ');
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    onClick={() => onToggle(p.id)}
                    className={cn(
                      'relative w-full rounded-lg border p-3 text-left',
                      on ? 'border-primary ring-1 ring-primary' : 'hover:bg-muted/50',
                    )}
                  >
                    <span className="flex justify-between gap-2">
                      <span className="font-medium">{p.serviceName}</span>
                      <span className="tabular-nums">{formatReais(packagePriceForSize(p.basePrice, p.sizePrices, size))}</span>
                    </span>
                    {meta && <span className="block text-xs text-muted-foreground">{meta}</span>}
                    {on && <Check size={14} className="absolute right-2 top-2 text-primary" aria-hidden />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {available.length > 0 && pending > 0 && (
          <p className="text-xs text-muted-foreground">
            {pending === 1 ? '1 pacote espera' : `${pending} pacotes esperam`} a confirmação do preço em Configurações → Mão de Obra.
          </p>
        )}

        {selected.length > 0 && (
          <p className="text-sm">
            Total dos pacotes: <span className="font-semibold tabular-nums">{formatReais(total)}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
